import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Gender, Species } from '@rehome/shared';
import { generateAnimalBio, createAnimal, attachIntakePhotos, toast, ADOPTER_PROFILE_FIELDS, canonicalizeTraits, normalizeTrait, useTraitVocabulary } from '@rehome/shared';
import { Sparkles, Loader2, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { SpeciesBreedSelect } from '../components/SpeciesBreedSelect';
import { PhotoUploadField, type PendingPhoto } from '../components/PhotoUploadField';

// ─── AddAnimalPage ─────────────────────────────────────────────────────────
// Quick animal form with AI bio generation
// From design_plan AddAnimal.tsx
// ===========================================================================

const AddAnimalPage: React.FC = () => {
  const navigate = useNavigate();
  const { shelterId } = useParams<{ shelterId: string }>();
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '', species: 'Kutya' as Species, breed: '', age: 0, gender: 'Kan' as Gender, traits: '', bio: '',
  });
  const [photos, setPhotos] = useState<PendingPhoto[]>([]);

  // A már használt kulcsszavak — ha a staff ezekből választ, a jellemző
  // azonnal egy szűrő-chipbe kerül (és nem kell AI-jal feloldani).
  const { vocabulary } = useTraitVocabulary();
  const enteredTraits = formData.traits.split(',').map(t => t.trim()).filter(Boolean);
  const traitSuggestions = vocabulary
    .filter(v => !enteredTraits.some(t => normalizeTrait(t) === normalizeTrait(v)))
    .slice(0, 12);

  const appendTrait = (trait: string) =>
    setFormData(prev => ({
      ...prev,
      traits: prev.traits.trim() ? `${prev.traits.replace(/,\s*$/, '')}, ${trait}` : trait,
    }));

  // Adoption requirements checklist
  const [showRequirements, setShowRequirements] = useState(false);
  const [requiredFields, setRequiredFields] = useState<string[]>([]);
  const [adoptionFee, setAdoptionFee] = useState<number>(15000);

  const toggleRequiredField = (key: string) => {
    setRequiredFields(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  // Group fields by category for display
  const categorizedFields = ADOPTER_PROFILE_FIELDS.reduce((acc, f) => {
    if (!acc[f.category]) acc[f.category] = [];
    acc[f.category].push(f);
    return acc;
  }, {} as Record<string, Array<typeof ADOPTER_PROFILE_FIELDS[number]>>);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGenerateBio = async () => {
    if (!formData.name || !formData.breed) { alert('Kérlek add meg a nevet és a fajtát!'); return; }
    setIsLoading(true);
    const traitsArray = formData.traits.split(',').map(t => t.trim()).filter(Boolean);
    const bio = await generateAnimalBio(formData.name, formData.species, formData.breed, traitsArray);
    setFormData(prev => ({ ...prev, bio }));
    setIsLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Add meg az állat nevét.');
      return;
    }
    if (!formData.breed.trim()) {
      toast.error('Válaszd ki (vagy add meg) a fajtát.');
      return;
    }
    if (photos.length === 0) {
      toast.error('Legalább 1 fotó feltöltése kötelező.');
      return;
    }
    setIsLoading(true);

    // Az azonosítót előre generáljuk, de a fotók csak a sor létrejötte UTÁN
    // tölthetők fel — l. attachIntakePhotos.
    const id = crypto.randomUUID();

    // A beírt jellemzőket a közös szótárhoz igazítjuk (pl. "kedves" ->
    // "Barátságos"), hogy a platform szűrőjében ne legyen belőlük két chip.
    const traits = await canonicalizeTraits(formData.traits.split(','));

    const result = await createAnimal({
      id,
      shelterId: shelterId || '',
      name: formData.name,
      species: formData.species,
      breed: formData.breed,
      age: Math.round(Number(formData.age)), // DB column is INTEGER
      gender: formData.gender,
      status: 'Available',
      traits,
      bio: formData.bio || null,
      intakeType: 'Surrender',
      intakeDate: new Date().toISOString().split('T')[0],
      imageUrl: null,
      gallery: [],
      privateGallery: [],
      // These were collected in the form but never saved before:
      requiredAdopterFields: requiredFields,
      adoptionFee: adoptionFee,
    });

    setIsLoading(false);
    if (result) {
      // A fotók csak a sor létrejötte után tölthetők fel (l. attachIntakePhotos).
      await attachIntakePhotos(id, photos.map(p => ({ dataUrl: p.dataUrl, isPublic: p.isPublic })));
      navigate('../animals');
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Új állat felvétele</h1>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Név</label>
              <input type="text" name="name" required className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white" value={formData.name} onChange={handleChange} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kor (év)</label>
                <input type="number" name="age" min="0" step="0.1" required className="w-full p-2 border border-gray-300 rounded-lg outline-none bg-white" value={formData.age} onChange={handleChange} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nem</label>
                <select name="gender" className="w-full p-2 border border-gray-300 rounded-lg outline-none bg-white" value={formData.gender} onChange={handleChange}>
                  <option value="Kan">Kan</option><option value="Nőstény">Nőstény</option>
                </select>
              </div>
            </div>
          </div>

          <SpeciesBreedSelect
            species={formData.species}
            breed={formData.breed}
            onSpeciesChange={species => setFormData(prev => ({ ...prev, species: species as Species }))}
            onBreedChange={breed => setFormData(prev => ({ ...prev, breed }))}
            inputClassName="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Jellemzők (vesszővel elválasztva)</label>
            <input type="text" name="traits" placeholder="Pl.: barátságos, gyerekbarát, nagy mozgásigényű" className="w-full p-2 border border-gray-300 rounded-lg outline-none bg-white" value={formData.traits} onChange={handleChange} />
            {traitSuggestions.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-gray-500 mb-1.5">Gyakran használt kulcsszavak (kattints a hozzáadáshoz):</p>
                <div className="flex flex-wrap gap-1.5">
                  {traitSuggestions.map(t => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => appendTrait(t)}
                      className="px-2.5 py-1 rounded-full text-xs font-medium border border-gray-300 text-gray-600 bg-white hover:bg-teal-50 hover:border-teal-300 hover:text-teal-700 transition-colors"
                    >
                      + {t}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <p className="mt-2 text-xs text-gray-400">
              A rokon értelmű szavakat automatikusan a már használt kulcsszóra cseréljük (pl. „kedves" → „Barátságos"), hogy a keresésben egy helyre kerüljenek. A bemutatkozás szövege nem változik.
            </p>
          </div>
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="block text-sm font-medium text-gray-700">Bemutatkozás (Bio)</label>
              <button type="button" onClick={handleGenerateBio} disabled={isLoading} className="text-xs flex items-center gap-1 text-purple-600 hover:text-purple-800 font-semibold bg-purple-50 px-2 py-1 rounded transition-colors">
                {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} AI Generálás
              </button>
            </div>
            <textarea name="bio" rows={4} className="w-full p-2 border border-gray-300 rounded-lg outline-none bg-white" value={formData.bio} onChange={handleChange} placeholder="Ide írhatsz kézzel, vagy generálhatsz..." />
          </div>

          {/* Photos — at least 1 required */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Fotók</label>
            <PhotoUploadField photos={photos} onChange={setPhotos} required />
          </div>

          {/* Adoption Requirements Checklist */}
          <div className="border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={() => setShowRequirements(!showRequirements)}
              className="flex items-center justify-between w-full text-left"
            >
              <div>
                <span className="text-sm font-semibold text-gray-800">Örökbefogadási követelmények</span>
                {requiredFields.length > 0 && (
                  <span className="ml-2 text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">
                    {requiredFields.length} mező kiválasztva
                  </span>
                )}
                <p className="text-xs text-gray-500 mt-0.5">Válaszd ki, milyen adatokat kérsz be az örökbefogadótól</p>
              </div>
              {showRequirements ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </button>

            {showRequirements && (
              <div className="mt-4 space-y-4">
                {/* Privacy warning */}
                <div className="bg-amber-50 border border-amber-200 text-amber-800 p-3 rounded-lg text-xs flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <strong>Figyelem:</strong> A csillaggal (*) jelölt mezők érzékeny személyes adatokat kérnek (pl. személyi igazolvány szám, anyagi háttér). Csak akkor kérd be őket, ha feltétlenül szükséges! A túl szigorú követelmények elriaszthatják az örökbefogadókat.
                  </div>
                </div>

                {/* Adoption fee */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Örökbefogadási díj (Ft)</label>
                  <input
                    type="number" min="0" step="1000"
                    value={adoptionFee}
                    onChange={e => setAdoptionFee(Number(e.target.value))}
                    className="w-48 p-2 border border-gray-300 rounded-lg text-sm outline-none bg-white"
                  />
                </div>

                {/* Checklist by category */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(categorizedFields).map(([category, fields]) => (
                    <div key={category} className="bg-gray-50 rounded-lg p-3">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{category}</h4>
                      <div className="space-y-1.5">
                        {fields.map(f => (
                          <label key={f.key} className="flex items-start gap-2 cursor-pointer hover:bg-white rounded p-1 transition-colors">
                            <input
                              type="checkbox"
                              checked={requiredFields.includes(f.key)}
                              onChange={() => toggleRequiredField(f.key)}
                              className="mt-0.5 h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                            />
                            <span className="text-sm text-gray-700">
                              {f.label}
                              {f.sensitive && <span className="text-amber-600 ml-0.5 text-xs" title="Érzékeny adat">*</span>}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Quick presets */}
                <div className="flex gap-2 flex-wrap">
                  <button type="button" onClick={() => setRequiredFields([])}
                    className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">
                    Összes törlése
                  </button>
                  <button type="button" onClick={() => setRequiredFields(ADOPTER_PROFILE_FIELDS.filter(f => !f.sensitive).map(f => f.key))}
                    className="text-xs px-3 py-1.5 rounded-lg border border-green-300 text-green-700 hover:bg-green-50">
                    Csak alap adatok (nem érzékeny)
                  </button>
                  <button type="button" onClick={() => setRequiredFields(ADOPTER_PROFILE_FIELDS.map(f => f.key))}
                    className="text-xs px-3 py-1.5 rounded-lg border border-red-300 text-red-700 hover:bg-red-50">
                    Minden adat (szigorú)
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="pt-4 flex justify-end gap-3">
            <button type="button" onClick={() => navigate('../animals')} className="px-4 py-2 text-gray-700 font-medium hover:bg-gray-100 rounded-lg">Mégse</button>
            <button type="submit" disabled={isLoading || photos.length === 0} className="px-6 py-2 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
              {isLoading ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Mentés...</span> : 'Állat Mentése'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAnimalPage;
